import React from 'react';
import { SystemStyleObject } from '@/styled-system/types';
import { Box, Text, ZillowLogo } from '@zillow/constellation-10';
import { BrandType, useBrandContext } from '~/core/components/BrandProvider';
import ShowingTimePlusLogo from '~/core/components/ShowingTimePlusLogo';

export const HEADER_HEIGHT = '4rem';

const MainHeader = ({
  showOnMobile,
  children,
  ...props
}: React.ComponentProps<typeof Box> & { showOnMobile: boolean }) => {
  return (
    <Box
      asChild
      css={{
        display: showOnMobile ? 'flex' : 'none',
        alignItems: 'center',
        padding: '0 2rem',
        height: HEADER_HEIGHT,
        backgroundColor: '{colors.bg.neutral}',
        borderBottom: '1px solid {colors.border.muted}',
        lg: {
          display: 'flex',
        },
      }}
      {...props}
    >
      <header>{children}</header>
    </Box>
  );
};

const HeaderLeft = ({
  children,
  ...props
}: React.ComponentProps<typeof Box>) => {
  return (
    <Box
      css={{
        flex: '0 0 40%',
        width: '40%',
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

const HeaderCenter = ({
  children,
  ...props
}: React.ComponentProps<typeof Box>) => {
  return (
    <Box
      css={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flex: '0 0 20%',
        width: '20%',
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

const HeaderRight = ({
  children,
  ...props
}: React.ComponentProps<typeof Box>) => {
  return (
    <Box
      css={{
        flex: '0 0 40%',
        width: '40%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

const Logo = ({ type, css }: { type: BrandType; css: SystemStyleObject }) => {
  if (type === BrandType.SHOWING_TIME_PLUS) {
    return <ShowingTimePlusLogo css={css} />;
  }

  return <ZillowLogo showLegacyLogo={type !== BrandType.ZILLOW} css={css} />;
};

interface Props {
  name?: string;
  showOnMobile?: boolean;
}

export function Header({ name, showOnMobile = false }: Props) {
  const brand = useBrandContext();

  return (
    <MainHeader showOnMobile={showOnMobile}>
      <HeaderLeft />
      <HeaderCenter>
        <Logo
          type={brand.type}
          css={{ height: `calc(${HEADER_HEIGHT} * 0.375)` }}
        />
      </HeaderCenter>
      <HeaderRight>
        <Text textStyle="body-sm-bold">{name}</Text>
      </HeaderRight>
    </MainHeader>
  );
}
